import type { LicenceList } from '../utils/normalize';
import { asClusterId } from '../../../access';
import type { Cluster } from '../types/domain.types';
import type { QueuedChange } from '../domain/licenceQueue';

import { USER_CENTER_ENDPOINTS } from './endpoints';
import { request } from './http';
import {
  clustersSchema,
  licenceListSchema,
  licencePreviewSchema,
  parseOrThrow,
  seedResultSchema,
  setLicencesResultSchema,
} from './schemas';
import type { LicencePreviewDto, SeedResultDto, SetLicencesResultDto } from './schemas';

export const fetchLicences = async (
  plantIds?: string[],
  signal?: AbortSignal,
): Promise<LicenceList> => {
  const params = plantIds && plantIds.length ? { plantIds: plantIds.join(',') } : undefined;
  const raw = await request('get', USER_CENTER_ENDPOINTS.LICENCES, { params, signal });
  return parseOrThrow(licenceListSchema, raw, 'licences');
};

export interface FetchClustersOptions {
  readonly q?: string;
  readonly limit?: number;
  readonly signal?: AbortSignal;
}

export const fetchClusters = async ({ q, limit, signal }: FetchClustersOptions = {}): Promise<Cluster[]> => {
  const trimmed = q?.trim();
  const params = {
    ...(trimmed ? { q: trimmed } : {}),
    ...(limit != null ? { limit } : {}),
  };
  const raw = await request('get', USER_CENTER_ENDPOINTS.CLUSTERS, {
    params: Object.keys(params).length ? params : undefined,
    signal,
  });
  return parseOrThrow(clustersSchema, raw, 'clusters').clusters.map((c) => ({
    id: asClusterId(c.id),
    name: c.name,
    plantCount: c.plantCount,
  }));
};

export const seedPlant = async (
  plantId: string,
  licensedModules?: readonly string[],
): Promise<SeedResultDto> => {
  const raw = await request('post', USER_CENTER_ENDPOINTS.LICENCE_SEED(plantId), {
    body: licensedModules ? { licensedModules: [...licensedModules] } : {},
  });
  return parseOrThrow(seedResultSchema, raw, 'seed plant');
};

export const previewLicence = async (
  plantId: string,
  change: QueuedChange,
  signal?: AbortSignal,
): Promise<LicencePreviewDto> => {
  const raw = await request('post', USER_CENTER_ENDPOINTS.LICENCE_PREVIEW(plantId), {
    body: { moduleKey: change.moduleKey, licensed: change.licensed },
    signal,
  });
  return parseOrThrow(licencePreviewSchema, raw, 'licence preview');
};

export interface SetLicencesInput {
  readonly changes: readonly QueuedChange[];
  readonly reason: string;
  readonly contractRef?: string | null;
}

export const setLicences = async (
  plantId: string,
  input: SetLicencesInput,
): Promise<SetLicencesResultDto> => {
  const raw = await request('patch', USER_CENTER_ENDPOINTS.LICENCE(plantId), {
    body: {
      changes: input.changes.map((c) => ({ moduleKey: c.moduleKey, licensed: c.licensed })),
      reason: input.reason,
      ...(input.contractRef ? { contractRef: input.contractRef } : {}),
    },
  });
  return parseOrThrow(setLicencesResultSchema, raw, 'set licences');
};
